import type { IconName } from "../../components/ui/icon";
import type { WidgetConfig } from "./types";
import type { WidgetModule } from "./widget-module";
import type { Omittable } from "../../lib/props";

/**
 * Add-Card preset — one tile in the view card gallery, flattened from each
 * widget module's `catalog` entries (stamped with the module's `type` and `icon`
 * fallback). Author a widget by adding a `catalog` entry to its module; nothing
 * here is hand-maintained. `section` is an open string the gallery groups by.
 */
export type WidgetPreset = Omittable<{
  key: string;
  type: string;
  label: string;
  description: string;
  icon: IconName;
  section: string;
  /** Data-source tags (opaque strings) shown as gallery chips + search terms. */
  tags?: readonly string[];
  defaultConfig: WidgetConfig;
  defaultW?: number;
  defaultH?: number;
  /** Whether an instance auto-titles from `label` (default true; false = stays
   *  untitled). Consumed by {@link presetInitialConfig}. (U2) */
  autoTitle?: boolean;
  /** Config keys this preset fixes on every instance created from it — see
   *  {@link WidgetCatalogEntry.locks}. Empty/absent: a free-form preset. */
  locks?: readonly string[];
}>;

/**
 * The config key that binds a placed widget to its preset. Three states:
 *
 *   a preset key   locked to that preset's `locks` ({@link presetLockedKeys})
 *   `null`         explicitly custom — a free-form preset, or a converted one
 *   absent         no stamp was ever written (a row persisted before stamps
 *                  existed, or a board seed). The registry's
 *                  `inferPresetFor` policy says whether such a row INFERS its
 *                  preset from its values ({@link inferPreset}); by default it
 *                  never does and the row is custom.
 *
 * Every gallery placement is stamped one way or the other. Converting to
 * custom writes `null`, never deletes the key — where inference is on, an
 * absent key would re-bind the lock on the next read.
 */
export const WIDGET_PRESET_KEY = "preset";

const NO_LOCKS: ReadonlySet<string> = new Set();

/**
 * The starting config for an instance created from a preset — THE one place
 * the naming and locking rules live (add and replace both call it).
 *
 * Naming: a curated preset lands named (its gallery label becomes the title)
 * unless it already declares a title OR opts out with `autoTitle: false`
 * (blank cards, notes, embeds, and every preset whose label IS its derived
 * title). `autoTitle` is the WHOLE rule: nothing here reads `section`, so a
 * section can be renamed or re-ordered without touching a single title. (U2)
 *
 * Locking: a preset with `locks` stamps its key under {@link WIDGET_PRESET_KEY}
 * so the instance stays bound to those locks after the gallery is gone.
 */
export function presetInitialConfig(preset: WidgetPreset): WidgetConfig {
  const config = { ...preset.defaultConfig } as WidgetConfig;
  const autoTitle = preset.autoTitle ?? true;
  if (autoTitle && typeof config.title !== "string") {
    config.title = preset.label;
  }
  config[WIDGET_PRESET_KEY] = hasLocks(preset) ? preset.key : null;
  return config;
}

type LockingPreset = WidgetPreset & { locks: readonly string[] };

function hasLocks(preset: WidgetPreset | undefined): preset is LockingPreset {
  return (preset?.locks?.length ?? 0) > 0;
}

/** Structural equality over the JSON-shaped values a `defaultConfig` holds. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, i) => sameValue(item, b[i]))
    );
  }
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return (
    ka.length === kb.length &&
    ka.every((k) =>
      sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    )
  );
}

/**
 * The locking preset an UNSTAMPED config was created from: among the presets
 * of the same module type, the one whose every locked value the config still
 * carries — the most specific match (most locks) when several fit. Values the
 * preset sets but does not lock (a donut toggle, a sort, the title) are not
 * consulted: they are the user's to change, so they cannot be evidence either
 * way. The registry decides WHICH unstamped configs reach this (see
 * `WidgetRegistryOptions.inferPresetFor`).
 */
export function inferPreset(
  presets: readonly WidgetPreset[],
  type: string,
  config: WidgetConfig,
): WidgetPreset | undefined {
  let best: LockingPreset | undefined;
  for (const preset of presets) {
    if (preset.type !== type || !hasLocks(preset)) continue;
    if (!preset.locks.every((key) => sameValue(config[key], preset.defaultConfig[key]))) continue;
    if (!best || preset.locks.length > best.locks.length) best = preset;
  }
  return best;
}

/**
 * The config keys a stored config may not edit: the `locks` of the preset it
 * was created from — read through its {@link WIDGET_PRESET_KEY} stamp, or,
 * when `infer` allows it for an unstamped row, {@link inferPreset}. Empty for
 * an explicitly custom instance, for an unstamped row inference does not
 * reach, for a stamp naming a preset that no longer exists (a retired preset
 * must not freeze a live widget), and for a preset that declares no locks.
 */
export function presetLockedKeys(
  presets: readonly WidgetPreset[],
  type: string,
  config: WidgetConfig | null | undefined,
  infer: (type: string, config: WidgetConfig) => boolean = () => false,
): ReadonlySet<string> {
  const stamp = config?.[WIDGET_PRESET_KEY];
  if (stamp === null) return NO_LOCKS;
  let preset: WidgetPreset | undefined;
  if (typeof stamp === "string") preset = presets.find((p) => p.key === stamp);
  else if (infer(type, config ?? {})) preset = inferPreset(presets, type, config ?? {});
  return hasLocks(preset) ? new Set(preset.locks) : NO_LOCKS;
}

/** Flatten a registry's modules into the Add-Card gallery presets. */
export function buildPresets(modules: WidgetModule[]): WidgetPreset[] {
  return modules.flatMap((def) =>
    (def.catalog ?? []).map((e) => ({
      key: e.key,
      type: def.type,
      label: e.label,
      description: e.description,
      icon: e.icon ?? def.icon,
      section: e.section,
      tags: e.tags,
      defaultConfig: (e.defaultConfig ?? {}) as WidgetConfig,
      defaultW: e.defaultW,
      defaultH: e.defaultH,
      autoTitle: e.autoTitle,
      locks: e.locks,
    })),
  );
}
